import type { Ceviri } from "../tip";
import type { rapor as tr } from "../tr/rapor";

const gunler = (n: number) => (n === 1 ? "day" : "days");

/** English dictionary · rapor. */
export const rapor: Ceviri<typeof tr> = {
  ekran: {
    baslik: "Holdings",
    aciklama: "Your portfolio's value, cost and realised return — transparent: every value shows where it came from.",
    tarihItibariyla: "As of date",
    gecmisGun: "Looking at a past date",
    bugun: "Today",
    raporParaBirimi: "Report currency",
    buguneDon: "Back to today",
    degerleniyor: "Valuing the portfolio…",
  },

  bos: {
    baslik: "Nothing to value",
    aciklama: "Once the vault has transactions, the portfolio's value, cost and realised return appear here.",
  },

  ust: {
    onEk: "As of ",
    ara: ", in ",
    sonEk: ".",
    nakitKapali: " Cash tracking is off (Settings).",
  },

  eksik: {
    baslik: "This total is incomplete",
    metin:
      'The positions below could not be valued, so they are NOT in the totals. Do not read the total as "the portfolio\'s value" — read it as incomplete.',
  },

  kart: {
    bolum: "Totals",
    piyasaDegeri: "Market value",
    gunlukGetiri: "Daily return",
    maliyet: "Cost",
    gerceklesmemisKazanc: "Unrealised gain",
    gerceklesmisKazanc: "Realised gain",
    nakitAkisiNet: "Cash flow (net)",
    gerceklesmisGetiri: "Realised return",
    asilGetiri: "True return",
    netNakitHareketi: "Net cash movement",
    yillikKarsilik: "Annualised",
    kesilenVergi: "Withholding tax",
    toplamMaliyet: "Total cost",
    toplamVarlik: "Total assets",
    getiri: "Return",
    mevduatKarsiligi: "Deposit equivalent",
    agirlikliOrtalama: "weighted average ",
    sonIslemGunu: (tarih: string) => `last trading day ${tarih}`,
    gelir: "income: ",
    gider: " · expense: ",
    yillik: "annualised ",
    gunSonEk: (n: number) => ` ${gunler(n)}`,
    netNakitAciklama:
      "The cash effect of every row in the vault. NOT A BALANCE: if you did not enter an opening amount the result is negative, and that is correct — the minus says money came from a source not recorded in the vault. Enter the opening amount as a deposit row and this value becomes the balance.",
    kovaSonEk: (n: number) => (n === 1 ? " bucket (account × currency)" : " buckets (account × currency)"),
  },

  terim: {
    deger: "value",
    maliyet: "cost",
    satisKazanci: "sale gain",
    degerNakitDahil: "value (incl. cash)",
    yatirilanCekilen: "deposited − withdrawn",
    oncekiGunDegeri: "previous day's value",
    cepten: "money out of pocket",
    satilan: "cost of lots sold",
    faiz: "interest",
    temettuFaiz: "dividend/interest",
    masraf: "fee",
    payda: "base",
    adetCarpi: " units × ",
    gunGetirisi: (tarih: string) => `return on ${tarih}`,
    bugunkuGetiri: "today's return",
    toplamVarlik: "total assets",
    toplamMaliyet: "total cost",
  },

  nakit: {
    bolum: "Net cash movement",
    eksikBaslik: "Cash total is incomplete",
    eksikMetin:
      'The buckets below could NOT be added to the total, so no total is published — showing an incomplete total as "net cash" would be misread wherever the warning next to it is not seen.',
    kasaSonHareket: (ref: string, tarih: string) => `vault ${ref} · last transaction ${tarih}`,
    tabloBasligi: "Net cash movement per account and currency",
    hesap: "Account",
    paraBirimi: "Currency",
    netHareket: "Net movement",
    karsiligi: (kod: string) => `In ${kod}`,
    okunamayanSatir: (n: number) =>
      `Amount could not be read on ${n} ${n === 1 ? "row" : "rows"}: the encoding rule is quantity = 1, price = amount, but the product is zero.`,
    turKirilimi: "Breakdown by type",
    netYok: "The bucket's net could not be calculated, so it has no converted value either.",
    kurYok: (kaynak: string, hedef: string, tarih: string) =>
      `No ${kaynak} → ${hedef} exchange rate found for ${tarih}. The value on the left is CORRECT; only its conversion is missing.`,
  },

  balon: {
    neden: "Why this value is missing",
    oranYok: "No rate: the base is zero or unknown.",
    bayat: "Stale measurement — where did this value come from?",
    nasil: "How was this value calculated?",
    motorSoylemedi: "The engine did not say why this value is missing — this is a defect, please report it.",
  },

  dayanak: "Basis of the value",
  seans: { pre: "pre-market", post: "after hours" },

  damga: {
    rapor: "Report flags",
    pozisyon: "Flags on this position",
  },

  tablo: {
    gorunumGrubu: "Phone layout",
    gorunum: "Layout:",
    tablo: "Table",
    kart: "Cards",
    satilmis: (n: number) => `${n} fully sold ${n === 1 ? "asset" : "assets"}:`,
    goster: "Show",
    toplamlarIcerir: "· Totals always include them.",
    deger: (kod: string) => `Value (${kod})`,
    maliyet: (kod: string) => `Cost (${kod})`,
    baslik: "Position valuations",
    varlik: "Asset",
    miktar: "Quantity",
    birimFiyat: "Unit price",
    gunluk: "Daily / %",
    mevcutGetiri: "Current return",
    gerceklesmis: "Realised",
    nakitAkisi: "Cash flow",
    gercGetiri: "Real. return",
    yillik: "Annualised",
    agrYas: "wtd. age",
    yillikAgrYas: "Annualised · wtd. age",
    damga: "Flags",
    pay: "Weight",
    seciliKapsamda: "in selected scope",
    paySeciliKapsamda: "Weight · in selected scope",
    sirala: (ad: string) => `Sort by ${ad}`,
    paydaEksik:
      "One asset's value could not be measured. Weights are based only on the total of assets that could be valued; that asset's row says why it has no weight.",
    gizle: "Hide",
  },

  yillik: {
    agrYasOnEk: "wtd. age ",
    gunSonEk: (n: number) => ` ${gunler(n)}`,
    nasil: "How was the annualised figure calculated?",
    getiriCarpi: " return × 365 ÷ ",
    gun: (sayi: string, n: number) => `${sayi} ${gunler(n)}`,
    payda: "Base: ",
    cepten: " money out of pocket",
    geriYatirilanOnEk: " (excluding reinvested ",
    geriYatirilanSonEk: ")",
  },

  gunluk: {
    sonGunAciklama: (tarih: string) =>
      `The value did not change today; shown is the return on the last trading day, ${tarih}.`,
    satirlarKarisik:
      "Some rows show their last trading day; this total is only today's change, so the rows do not add up to it.",
    kurEtkisi: "of which exchange rate effect: ",
    sonIslemGunu: (tarih: string) => `Last trading day ${tarih}`,
  },

  ayrinti: {
    birimFiyat: "Unit price",
    kesilenVergi: "Withholding tax",
    kesintiYok: "No transactions with withholding information.",
    acikPartiler: "Open lots (FIFO)",
    edinme: "Acquired",
    miktar: "Quantity",
    maliyet: "Cost",
    not: "Note",
    maliyetBilinmiyor: "cost unknown — not counted as zero",
    satislar: "Sales — how much from which lot",
    adetHasilat: " units · proceeds ",
    maliyetAra: " · cost ",
    kazancAra: " · gain ",
    maliyetEksik: "cost incomplete",
    partisinden: " lot: ",
    adetMaliyet: " units, cost ",
    bilinmiyor: " (unknown)",
  },

  nakitDokumu: {
    yok: "This asset has no dividend, interest or fee transactions.",
    baslik: "Cash flow breakdown",
    gelir: "income ",
    gider: " · expense ",
    net: " · net ",
    tarih: "Date",
    tur: "Type",
    tutar: "Amount",
    stopaj: "Withholding tax",
    durum: "Status",
    okunamadi: "unreadable",
    miktarYazilmamis: "quantity missing — row not in total",
    toplamaGirdi: "in total",
  },

  pozisyonlar: {
    baslik: "Positions",
    aciklama: "Raw quantities derived from transactions.",
    tarihItibariyla: "As of date",
    buguneDon: "Back to today",
    karZararYok: "No profit/loss on this screen",
    karZararMetni:
      "Stage 1 shows quantities only. Cost, return and valuation would be wrong without corporate actions and exchange rate data — they arrive in Stage 3. The quantities here are raw totals too: split/bonus share adjustments are NOT applied.",
    hesaplaniyor: "Calculating positions…",
    bosBaslik: "No positions",
    bosAciklama: "Appears here once a transaction that carries a quantity (buy, sell, transfer, bonus shares) is entered.",
    tabloBasligi: "Raw positions",
    varlik: "Asset",
    baglama: "Link",
    hamMiktar: "Raw quantity",
    paraBirimi: "Currency",
    ilkIslem: "First trade",
    sonIslem: "Last trade",
    hareket: "Transactions",
    izlenen: "Tracked instrument",
    elle: "Manual",
  },

  temettuHaric: {
    balon: "Dividends were left out by the rule; the result is the price return.",
    neden: "Excluding dividends — why?",
    etiket: "excl. dividends",
  },

  bilgi: {
    nasil: (ad: string) => `How is ${ad} calculated?`,
    uyariVar: (ad: string) => `${ad} — warning; how is it calculated?`,
  },
};
